/**
 * @format
 */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
  Divider,
  Modal,
  Fade,
  Backdrop,
  List,
  ListItem,
  Stack,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { useAuth } from '../../../../../../app/context/AuthContext';
import { MODULE_LIST } from '../../../../../../constants/commons';
import { getActivationAssets } from '../../../../../../core/services/projectWorkRoom.service';

/**
 * ActivationCard
 *
 * Read-only display of an activation inside a Modal. All editing is handled
 * elsewhere (a separate modal/screen the parent navigates to) — this
 * component only renders data and forwards clicks via `onEditClick`.
 *
 * Props:
 * - open: boolean            controls the Modal (owned by the parent)
 * - onClose: () => void      called on backdrop click / close icon
 * - data: {...}              the activation payload (see shape below)
 * - onEditClick: (data) => void
 *     fired from every edit-style affordance: "edit activation",
 *     "edit variants", and "add files". Same handler everywhere —
 *     the parent decides where to navigate based on `data`.
 *
 * Expected `data` shape:
 * {
 *   activationId: number,
 *   activationName: string,
 *   statusId: number,                // 1 = Draft, 2 = In Review, 3 = Approved
 *   description: string,
 *   activationPoints: string[],      // may arrive JSON-quoted, e.g. "\"6-15s\""
 *   projectId: number,
 *   fileName: string[],
 *   baseUrl: string,
 *   variants: [{
 *     variantsId: number,
 *     variantsTitle: string,
 *     variantsDescription: string,
 *     variantsPoints: string[],
 *     hookName: string
 *   }]
 * }
 */

const STATUS_LABELS = {
  1: { label: 'Draft', color: 'default' },
  2: { label: 'In Review', color: 'warning' },
  3: { label: 'Approved', color: 'success' },
};

// Strips accidental JSON-style quoting like `"\"6-15s optimal\""` -> `6-15s optimal`
const cleanText = (value) => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
    return trimmed.slice(1, -1);
  }
  return trimmed;
};

function FileThumb({ activationId, name }) {
  const [blob, setBlob] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [largeView, setLargeView] = useState(false);

  async function fetchAsset() {
    const { data } = await getActivationAssets(activationId, name);
    const url = URL.createObjectURL(data);
    setBlob(url);
    setIsLoading(false);
  }

  useEffect(() => {
    fetchAsset();
  }, []);

  return (
    <>
      <Box
        sx={{
          width: 120,
          height: 120,
          borderRadius: 1.5,
          overflow: 'hidden',
          border: '1px solid #cac8bf',
          bgcolor: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        title={name}
      >
        {!isLoading ? (
          <Box
            component="img"
            src={blob}
            alt={name}
            onError={() => setIsLoading(false)}
            onClick={() => setLargeView(true)}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
              cursor: 'pointer',
            }}
          />
        ) : (
          <InsertDriveFileIcon sx={{ color: '#9a988c', fontSize: 34 }} />
        )}
      </Box>
      <Modal
        open={largeView}
        onClose={() => setLargeView(false)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          component="img"
          src={blob}
          alt={name}
          sx={{
            maxWidth: '90vw',
            maxHeight: '90vh',
            objectFit: 'contain',
          }}
        />
      </Modal>
    </>
  );
}

export default function ActivationCard({ open, onClose, data, onEditClick }) {
  const { userPermissions } = useAuth();
  if (!data) return null;

  const {
    activationId,
    activationName,
    statusId,
    description,
    activationPoints = [],
    fileName = [],
    variants = [],
  } = data;

  const status = STATUS_LABELS[statusId] || {
    label: 'Unknown',
    color: 'default',
  };

  const activationModule = Object.hasOwn(
    userPermissions,
    MODULE_LIST.ACTIVATION
  )
    ? userPermissions[MODULE_LIST.ACTIVATION]
    : false;

  const canEditActivation = activationModule.edit;

  function editButton(text, clickHandler) {
    return (
      <Button
        size="small"
        startIcon={<EditIcon fontSize="small" />}
        sx={{
          mt: 1.5,
          color: '#555349',
          textTransform: 'none',
          px: 0,
          display: canEditActivation ? 'auto' : 'none',
        }}
        onClick={clickHandler}
      >
        {text}
      </Button>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{ backdrop: { timeout: 300 } }}
    >
      <Fade in={open}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 'min(920px, 92vw)',
            bgcolor: '#e9e7e2',
            borderRadius: 3,
            boxShadow: 24,
            outline: 'none',
            maxHeight: '90vh',
            overflowY: 'auto',
          }}
        >
          {/* Header */}
          <Box sx={{ px: 3, pt: 3, pb: 2 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap={1.5}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {activationName}
                </Typography>
                {/* <Chip label={status.label} color={status.color} size="small" /> */}
              </Stack>
              <Stack direction="row" spacing={1}>
                <IconButton size="small" aria-label="Close" onClick={onClose}>
                  <CloseIcon />
                </IconButton>
              </Stack>
            </Stack>

            <List sx={{ listStyleType: 'disc', pl: 3, py: 1 }}>
              {activationPoints.map((point, i) => (
                <ListItem
                  key={i}
                  sx={{ display: 'list-item', p: 0, fontSize: 15 }}
                >
                  {cleanText(point)}
                </ListItem>
              ))}
            </List>

            {description && (
              <Typography variant="body1" sx={{ mt: 1 }}>
                {description}
              </Typography>
            )}

            {editButton('edit activation', () => onEditClick(data, 1))}
          </Box>

          <Divider sx={{ mx: 3 }} />

          {/* Variants + Files */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={3}
            sx={{ px: 3, py: 2.5 }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" sx={{ color: '#6b6a60' }}>
                Variants:
              </Typography>

              {variants.length === 0 && (
                <Typography
                  variant="body2"
                  sx={{ color: '#8a897d', fontStyle: 'italic', mt: 1 }}
                >
                  No variants added yet.
                </Typography>
              )}

              {variants.map((variant) => (
                <Box key={variant.variantsId} sx={{ mt: 1.5 }}>
                  <Typography sx={{ fontWeight: 700 }}>
                    {variant.variantsTitle}
                  </Typography>

                  {variant.hookName && (
                    <>
                      <Typography variant="body2" sx={{ color: '#3c3b34' }}>
                        Hook (Frame 1):
                      </Typography>
                      <Typography variant="body2">
                        {variant.hookName}
                      </Typography>
                    </>
                  )}

                  {variant.variantsDescription && (
                    <Typography
                      variant="body2"
                      sx={{ color: '#4a4940', mt: 0.5 }}
                    >
                      {variant.variantsDescription}
                    </Typography>
                  )}

                  {variant.variantsPoints?.length > 0 && (
                    <List sx={{ listStyleType: 'disc', pl: 2.5, py: 0.5 }}>
                      {variant.variantsPoints.map((p, i) => (
                        <ListItem
                          key={i}
                          sx={{ display: 'list-item', p: 0, fontSize: 13 }}
                        >
                          {cleanText(p)}
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              ))}

              {editButton(
                'edit variants',
                () => onEditClick && onEditClick(data, 2)
              )}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="caption" sx={{ color: '#6b6a60' }}>
                  Files:
                </Typography>
                {editButton(
                  'add files',
                  () => onEditClick && onEditClick(data, 3)
                )}
              </Stack>

              <Box
                sx={{
                  bgcolor: '#dcdad3',
                  borderRadius: 2,
                  minHeight: 140,
                  p: 2,
                  mt: 1,
                }}
              >
                {fileName.length === 0 ? (
                  <Typography
                    variant="body2"
                    sx={{ color: '#8a897d', fontStyle: 'italic' }}
                  >
                    No files attached.
                  </Typography>
                ) : (
                  <Stack direction="row" flexWrap="wrap" gap={1.5}>
                    {fileName.map((name) => (
                      <FileThumb
                        key={name}
                        activationId={activationId}
                        name={name}
                      />
                    ))}
                  </Stack>
                )}
              </Box>
            </Box>
          </Stack>
        </Box>
      </Fade>
    </Modal>
  );
}
